import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const CommunitySection = ({ challenges, leaderboard, announcements }) => {
  const [activeTab, setActiveTab] = useState('challenges');

  const tabs = [
    { id: 'challenges', label: 'Challenges', icon: 'Trophy' },
    { id: 'leaderboard', label: 'Leaderboard', icon: 'Award' },
    { id: 'announcements', label: 'News', icon: 'Bell' }
  ];

  return (
    <div className="card">
      <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground mb-6">
        Community
      </h2>
      <div className="flex gap-2 mb-6 overflow-x-auto custom-scrollbar pb-2">
        {tabs?.map((tab) => (
          <button
            key={tab?.id}
            onClick={() => setActiveTab(tab?.id)}
            className={`
              flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap
              transition-all duration-250 flex-shrink-0
              ${activeTab === tab?.id
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-foreground hover:bg-muted/80'
              }
            `}
          >
            <Icon name={tab?.icon} size={18} />
            <span className="text-sm font-medium">{tab?.label}</span>
          </button>
        ))}
      </div>
      {activeTab === 'challenges' && (
        <div className="space-y-4">
          {challenges?.map((challenge) => {
            const progressPercent = (challenge?.currentProgress / challenge?.goal) * 100;
            
            return (
              <div
                key={challenge?.id}
                className="p-4 rounded-lg border border-border hover:border-primary/50 transition-all duration-250"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                      <Icon name="Trophy" size={20} color="var(--color-accent)" />
                    </div>
                    <div>
                      <h3 className="text-base font-medium text-foreground mb-1">
                        {challenge?.name}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {challenge?.description}
                      </p>
                    </div>
                  </div>
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    {challenge?.daysLeft} days left
                  </span>
                </div>
                <div className="mb-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-foreground font-medium">
                      {challenge?.currentProgress} / {challenge?.goal} {challenge?.unit}
                    </span>
                    <span className="text-sm text-primary font-medium">
                      {Math.round(progressPercent)}%
                    </span>
                  </div>
                  <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-primary to-accent transition-all duration-500"
                      style={{ width: `${Math.min(progressPercent, 100)}%` }}
                    />
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">
                    {challenge?.participants} participants
                  </span>
                  <Button variant="ghost" size="sm" iconName="ArrowRight" iconPosition="right">
                    View Details
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {activeTab === 'leaderboard' && (
        <div className="space-y-3">
          {leaderboard?.map((member, index) => (
            <div
              key={member?.id}
              className={`
                flex items-center gap-4 p-4 rounded-lg
                ${index < 3 ? 'bg-gradient-to-r from-accent/5 to-primary/5 border border-primary/20' : 'bg-muted'}
              `}
            >
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <span className={`
                  text-lg font-bold data-text flex-shrink-0 w-8 text-center
                  ${index === 0 ? 'text-accent' : index === 1 ? 'text-muted-foreground' : index === 2 ? 'text-warning' : 'text-muted-foreground'}
                `}>
                  #{index + 1}
                </span>
                
                <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                  <Image
                    src={member?.avatar}
                    alt={member?.avatarAlt}
                    className="w-full h-full object-cover"
                  />
                </div>

                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">
                    {member?.name}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {member?.workouts} workouts
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 flex-shrink-0">
                <Icon name="Flame" size={16} color="var(--color-accent)" />
                <span className="text-sm font-bold text-foreground data-text">
                  {member?.points}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
      {activeTab === 'announcements' && (
        <div className="space-y-4">
          {announcements?.map((announcement) => (
            <div
              key={announcement?.id}
              className="p-4 rounded-lg border border-border hover:border-primary/50 transition-all duration-250"
            >
              <div className="flex items-start gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  announcement?.type === 'event' ? 'bg-primary/10' :
                  announcement?.type === 'maintenance'? 'bg-warning/10' : 'bg-success/10'
                }`}>
                  <Icon 
                    name={
                      announcement?.type === 'event' ? 'Calendar' :
                      announcement?.type === 'maintenance'? 'AlertTriangle' : 'Info'
                    }
                    size={20}
                    color={
                      announcement?.type === 'event' ? 'var(--color-primary)' :
                      announcement?.type === 'maintenance' ? 'var(--color-warning)' :
                      'var(--color-success)'
                    }
                  />
                </div>

                <div className="flex-1 min-w-0">
                  <h3 className="text-base font-medium text-foreground mb-1">
                    {announcement?.title}
                  </h3>
                  <p className="text-sm text-muted-foreground mb-2 line-clamp-2">
                    {announcement?.message}
                  </p>
                  <span className="text-xs text-muted-foreground">
                    {announcement?.date}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CommunitySection;